import { Box, Container, Grid, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";

const stats = [
  { value: "43+", label: "Happy Customers" },
  { value: "7+", label: "Years of Experience" },
  { value: "56+", label: "Consultants & Experts" },
  { value: "115+", label: "Projects Completed" },
];

const CountUp = ({ value, duration = 1800 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  const number = parseInt(value, 10);
  const suffix = value.replace(number, "");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;

          let start = 0;
          const increment = number / (duration / 16);

          const timer = setInterval(() => {
            start += increment;

            if (start >= number) {
              setCount(number);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.4 },
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, [number, duration]);

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
};

const AboutStory = () => {
  return (
    <Box component="section" sx={styles.section}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
          <Grid item size={{ xs: 12, md: 6 }}>
            <Box sx={styles.badge}>About Our Company</Box>

            <Typography variant="h3" sx={styles.title}>
              Our Story
            </Typography>

            <Typography sx={styles.text}>
              ZENCON Ltd. Co. is a leading Business & Technology consulting
              company delivering SAP services, enterprise solutions, digital
              transformation, and industry-specific innovations across global
              markets.
            </Typography>

            <Typography sx={styles.text}>
              ZENCON Ltd. Co. was established in Saudi Arabia in 2019 to serve
              the Kingdom's rapidly growing digital transformation market with
              specialized SAP solutions. We are committed to helping
              organizations accelerate their digital transformation through
              reliable, scalable, and business-focused SAP solutions, backed by
              extensive SAP implementation experience and deep functional
              expertise across SAP S/4HANA, FICO, MM, and HCM.
            </Typography>

            <Typography sx={styles.text}>
              At ZENCON, we believe that creating value by harmonizing domain
              knowledge, enterprise technologies, and digital innovations to
              reimagine business possibilities. Since our inception, we have
              remained committed to transforming small, medium, and enterprise
              organizations companies by redefining ERP and delivering
              innovative, tailored, and streamlined solutions.
            </Typography>

            <Typography sx={styles.text}>
              Our approach has enabled our clients to become more efficient,
              simplify business operations, enhance supply chain visibility,
              make informed decisions, and optimize return on investment.
            </Typography>
          </Grid>

          <Grid item size={{ xs: 12, md: 6 }}>
            <Box sx={styles.visualCard}>
              <Box sx={styles.orbitWrap}>
                <Box sx={{ ...styles.orbit, ...styles.orbitOne }} />
                <Box sx={{ ...styles.orbit, ...styles.orbitTwo }} />

                <Box sx={styles.core}>
                  <Typography sx={styles.coreText}>ZENCON</Typography>
                  <Typography sx={styles.coreSubText}>
                    Business + Technology
                  </Typography>
                </Box>

                <Box sx={{ ...styles.floatNode, ...styles.nodeOne }}>ERP</Box>
                <Box sx={{ ...styles.floatNode, ...styles.nodeTwo }}>SAP</Box>
                <Box sx={{ ...styles.floatNode, ...styles.nodeThree }}>AI</Box>
                <Box sx={{ ...styles.floatNode, ...styles.nodeFour }}>BI</Box>
              </Box>

              <Grid container spacing={2} sx={{ mt: { xs: 3, md: 5 } }}>
                {stats.map((item, index) => (
                  <Grid item size={{ xs: 6, md: 6 }} key={index}>
                    <Box sx={styles.statCard}>
                      <Typography sx={styles.statValue}>
                        <CountUp value={item.value} />
                      </Typography>

                      <Typography sx={styles.statLabel}>
                        {item.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const styles = {
  section: {
    position: "relative",
    py: { xs: 7, md: 11 },
    overflow: "hidden",
    background:
      "radial-gradient(circle at top left, rgba(0,200,83,0.12), transparent 34%), linear-gradient(135deg, #071525 0%, #082d63 100%)",
    color: "#fff",
    textAlign: "justify",
  },

  badge: {
    display: "inline-flex",
    px: 2,
    py: 0.8,
    mb: 2,
    borderRadius: "999px",
    fontSize: 13,
    fontWeight: 700,
    color: "#00c853",
    background: "rgba(0,200,83,0.12)",
    border: "1px solid rgba(0,200,83,0.28)",
  },

  title: {
    fontSize: { xs: "2rem", sm: "2.4rem", md: "3rem" },
    fontWeight: 600,
    lineHeight: 1.05,
    mb: 1.5,
    letterSpacing: "-1.5px",
  },

  text: {
    color: "rgba(255,255,255,0.78)",
    fontSize: { xs: "0.8rem", md: "1rem" },
    lineHeight: 1.5,
    mb: 2,
  },

  visualCard: {
    position: "relative",
    p: { xs: 2.5, sm: 4 },
    borderRadius: 6,
    background: "transparent",
    backdropFilter: "none",
    boxShadow: "none",
    border: "none",
    overflow: "hidden",

    "&::before": {
      display: "none",
    },
  },

  orbitWrap: {
    position: "relative",
    height: { xs: 310, sm: 360 },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  orbit: {
    position: "absolute",
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.18)",
    animation: "spin 18s linear infinite",

    "@keyframes spin": {
      from: { transform: "rotate(0deg)" },
      to: { transform: "rotate(360deg)" },
    },
  },

  orbitOne: {
    width: 230,
    height: 230,
  },

  orbitTwo: {
    width: 320,
    height: 320,
    animationDuration: "28s",
    animationDirection: "reverse",
  },

  core: {
    width: { xs: 170, sm: 210 },
    height: { xs: 170, sm: 210 },
    borderRadius: "50%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    background:
      "radial-gradient(circle, rgba(0,200,83,0.26), rgba(0,80,180,0.18))",
    border: "1px solid rgba(255,255,255,0.25)",
    boxShadow:
      "0 0 45px rgba(0,200,83,0.25), inset 0 0 35px rgba(255,255,255,0.08)",
    animation: "pulse 3.4s ease-in-out infinite",

    "@keyframes pulse": {
      "0%, 100%": { transform: "scale(1)" },
      "50%": { transform: "scale(1.04)" },
    },
  },

  coreText: {
    fontSize: { xs: 25, sm: 31 },
    fontWeight: 900,
    letterSpacing: 1,
  },

  coreSubText: {
    mt: 1,
    maxWidth: 140,
    fontSize: 13,
    color: "rgba(255,255,255,0.72)",
  },

  floatNode: {
    position: "absolute",
    width: { xs: 58, sm: 68 },
    height: { xs: 58, sm: 68 },
    borderRadius: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    color: "#fff",
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.2)",
    boxShadow: "0 14px 35px rgba(0,0,0,0.28)",
    animation: "float 4s ease-in-out infinite",

    "@keyframes float": {
      "0%, 100%": { transform: "translateY(0)" },
      "50%": { transform: "translateY(-14px)" },
    },
  },

  nodeOne: {
    top: 25,
    left: { xs: 22, sm: 60 },
  },

  nodeTwo: {
    top: 40,
    right: { xs: 24, sm: 72 },
    animationDelay: "0.6s",
  },

  nodeThree: {
    bottom: 38,
    left: { xs: 34, sm: 90 },
    animationDelay: "1s",
  },

  nodeFour: {
    bottom: 42,
    right: { xs: 38, sm: 92 },
    animationDelay: "1.4s",
  },

  statCard: {
    position: "relative",
    p: { xs: 2, sm: 2.5 },
    borderRadius: 5,
    minHeight: 120,
    overflow: "hidden",
    background:
      "linear-gradient(145deg, rgba(0,200,83,0.18), rgba(255,255,255,0.08))",
    border: "1px solid rgba(255,255,255,0.18)",
    backdropFilter: "blur(18px)",
    boxShadow: "0 18px 45px rgba(0,0,0,0.22)",
    transition: "0.35s ease",

    "&::before": {
      content: '""',
      position: "absolute",
      width: 90,
      height: 90,
      borderRadius: "50%",
      right: -30,
      top: -30,
      background: "rgba(0,200,83,0.22)",
      filter: "blur(8px)",
    },

    "&:hover": {
      transform: "translateY(-7px)",
      boxShadow: "0 24px 60px rgba(0,200,83,0.22)",
      borderColor: "rgba(0,200,83,0.42)",
    },
  },

  statValue: {
    position: "relative",
    zIndex: 1,
    fontSize: { xs: 30, sm: 38 },
    fontWeight: 700,
    color: "#ffffff",
    lineHeight: 1,
    mb: 1.2,
    textShadow: "0 6px 22px rgba(0,0,0,0.25)",
  },

  statLabel: {
    position: "relative",
    zIndex: 1,
    fontSize: { xs: 13, sm: 15 },
    fontWeight: 700,
    lineHeight: 1.35,
    color: "rgba(255,255,255,0.78)",
  },
};

export default AboutStory;
